import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Megaphone,
  Plus,
  Pause,
  Play,
  X,
  Sparkles,
  TrendingUp,
  Music,
} from "lucide-react";

const GENRE_CHOICES = [
  "Hip-Hop",
  "Pop",
  "R&B",
  "Rock",
  "Electronic",
  "Country",
  "Latin",
  "Afrobeats",
  "Indie",
  "Jazz",
  "Reggae",
  "Classical",
];

const BUDGET_PRESETS = [5, 10, 15, 20, 30];

const CampaignRow = ({ campaign, onPause, onCancel }) => {
  const pct = Math.min(100, Math.round((campaign.spent_usd / campaign.budget_usd) * 100));
  const statusColors = {
    active: "text-emerald-300 bg-emerald-300/10 border-emerald-300/30",
    paused: "text-amber-300 bg-amber-300/10 border-amber-300/30",
    exhausted: "text-white/40 bg-white/5 border-white/15",
    cancelled: "text-white/30 bg-white/5 border-white/10",
  };
  return (
    <div
      data-testid={`promo-campaign-${campaign.campaign_id}`}
      className="bg-white/[0.03] border border-white/10 rounded-2xl p-5"
    >
      <div className="flex items-start gap-4">
        {campaign.artwork_url ? (
          <img
            src={campaign.artwork_url}
            alt={campaign.track_name}
            className="w-14 h-14 rounded-xl object-cover"
          />
        ) : (
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#B580FF]/40 to-[#5E8BFF]/40 flex items-center justify-center">
            <Music size={22} className="text-white/70" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-semibold truncate">{campaign.track_name}</div>
            <span
              className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border font-semibold ${
                statusColors[campaign.status] || statusColors.cancelled
              }`}
            >
              {campaign.status}
            </span>
          </div>
          <div className="text-xs text-white/50 mt-0.5">
            {campaign.genre_tags?.length
              ? `Targeting: ${campaign.genre_tags.join(", ")}`
              : "Broad targeting"}
          </div>
        </div>
        <div className="flex gap-1.5">
          {(campaign.status === "active" || campaign.status === "paused") && (
            <button
              data-testid={`pause-promo-${campaign.campaign_id}`}
              onClick={() => onPause(campaign.campaign_id)}
              className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/15 flex items-center justify-center transition"
              title={campaign.status === "active" ? "Pause" : "Resume"}
            >
              {campaign.status === "active" ? (
                <Pause size={14} />
              ) : (
                <Play size={14} className="ml-0.5" />
              )}
            </button>
          )}
          {campaign.remaining_usd > 0 && campaign.status !== "exhausted" && (
            <button
              data-testid={`cancel-promo-${campaign.campaign_id}`}
              onClick={() => onCancel(campaign.campaign_id)}
              className="w-9 h-9 rounded-full bg-white/5 hover:bg-rose-500/30 flex items-center justify-center transition"
              title="Cancel & refund remaining"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Budget progress bar */}
      <div className="mt-4">
        <div className="flex items-center justify-between text-xs mb-1.5 text-white/55">
          <span>
            ${campaign.spent_usd.toFixed(2)} spent of ${campaign.budget_usd.toFixed(2)}
          </span>
          <span>{pct}%</span>
        </div>
        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#B580FF] to-[#5E8BFF] rounded-full transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Stats row */}
      <div className="mt-4 grid grid-cols-3 gap-3 text-center">
        <div>
          <div className="font-display text-lg font-semibold tabular-nums">
            {(campaign.impressions || 0).toLocaleString()}
          </div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mt-0.5">
            Impressions
          </div>
        </div>
        <div>
          <div className="font-display text-lg font-semibold tabular-nums">
            {(campaign.clicks || 0).toLocaleString()}
          </div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mt-0.5">
            Clicks
          </div>
        </div>
        <div>
          <div className="font-display text-lg font-semibold tabular-nums text-[#E2FF31]">
            {campaign.click_through_pct.toFixed(1)}%
          </div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mt-0.5">
            CTR
          </div>
        </div>
      </div>
    </div>
  );
};

const NewCampaignForm = ({ onCancel, onCreated }) => {
  const [tracks, setTracks] = useState([]);
  const [trackId, setTrackId] = useState("");
  const [genres, setGenres] = useState([]);
  const [budget, setBudget] = useState(10);
  const [pitch, setPitch] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api
      .get("/profile/me")
      .then((r) => {
        const ups = r.data?.uploads || [];
        setTracks(ups);
        if (ups.length && !trackId) setTrackId(ups[0].trackId);
      })
      .catch(() => {});
  }, []);

  const toggleGenre = (g) => {
    setGenres((cur) => (cur.includes(g) ? cur.filter((x) => x !== g) : [...cur, g]));
  };

  const submit = async () => {
    const track = tracks.find((t) => t.trackId === trackId);
    if (!track) {
      toast.error("Select a track to promote");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/ads/promote", {
        track_id: String(track.trackId),
        track_name: track.trackName,
        artist_name: track.artistName,
        artwork_url: track.artworkUrl100,
        preview_url: track.previewUrl,
        genre_tags: genres,
        budget_usd: Number(budget),
        pitch: pitch.trim() || undefined,
      });
      toast.success("Campaign launched!", {
        description: `$${Number(budget).toFixed(2)} budget · ${
          genres.length || "broad"
        } targeting`,
      });
      onCreated(r.data.campaign);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't launch campaign");
    } finally {
      setSubmitting(false);
    }
  };

  if (tracks.length === 0) {
    return (
      <div
        data-testid="promo-no-tracks"
        className="text-center bg-white/[0.03] border border-white/10 rounded-3xl p-8"
      >
        <Music size={32} className="mx-auto text-white/30 mb-3" />
        <h3 className="font-semibold mb-1">Upload a track first</h3>
        <p className="text-white/50 text-sm">You need at least one uploaded track to promote.</p>
      </div>
    );
  }

  return (
    <div
      data-testid="promo-new-form"
      className="bg-white/[0.03] border border-white/10 rounded-3xl p-6"
    >
      <h3 className="font-display text-xl font-semibold mb-4">New campaign</h3>

      {/* Track picker */}
      <div className="mb-5">
        <div className="text-xs uppercase tracking-[0.18em] text-white/55 mb-2">Track</div>
        <select
          data-testid="promo-track-select"
          value={trackId}
          onChange={(e) => setTrackId(e.target.value)}
          className="w-full bg-black/40 border border-white/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#B580FF]/60"
        >
          {tracks.map((t) => (
            <option key={t.trackId} value={t.trackId}>
              {t.trackName}
            </option>
          ))}
        </select>
      </div>

      {/* Genre selector */}
      <div className="mb-5">
        <div className="text-xs uppercase tracking-[0.18em] text-white/55 mb-2">
          Genres (multi-select — leave blank for broad targeting)
        </div>
        <div className="flex flex-wrap gap-2">
          {GENRE_CHOICES.map((g) => (
            <button
              key={g}
              data-testid={`promo-genre-${g.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => toggleGenre(g)}
              className={`text-sm px-3.5 py-1.5 rounded-full border transition ${
                genres.includes(g)
                  ? "bg-[#B580FF] text-black border-[#B580FF]"
                  : "border-white/15 text-white/70 hover:border-white/35"
              }`}
            >
              {g}
            </button>
          ))}
        </div>
      </div>

      {/* Budget */}
      <div className="mb-5">
        <div className="text-xs uppercase tracking-[0.18em] text-white/55 mb-2">
          Budget · $0.05 per impression
        </div>
        <div className="flex gap-2 flex-wrap mb-3">
          {BUDGET_PRESETS.map((b) => (
            <button
              key={b}
              data-testid={`promo-budget-${b}`}
              onClick={() => setBudget(b)}
              className={`px-4 py-2 rounded-full border transition text-sm font-semibold ${
                Number(budget) === b
                  ? "bg-white text-black border-white"
                  : "border-white/15 text-white/70 hover:border-white/35"
              }`}
            >
              ${b}
            </button>
          ))}
        </div>
        <input
          data-testid="promo-budget-custom"
          type="number"
          min="5"
          max="30"
          step="1"
          value={budget}
          onChange={(e) => setBudget(Number(e.target.value))}
          className="w-full bg-black/40 border border-white/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-vermilion/60"
          placeholder="Custom budget ($5–$30)"
        />
        <p className="text-xs text-white/45 mt-2">
          Estimated reach: ~{Math.floor((budget / 0.05) || 0).toLocaleString()} impressions
        </p>
      </div>

      {/* Pitch (optional) */}
      <div className="mb-5">
        <div className="text-xs uppercase tracking-[0.18em] text-white/55 mb-2">
          Pitch (optional — what listeners hear)
        </div>
        <textarea
          data-testid="promo-pitch"
          value={pitch}
          onChange={(e) => setPitch(e.target.value)}
          rows={3}
          maxLength={180}
          placeholder="e.g. New from the underground — 15 seconds of fire on 1o1sound."
          className="w-full bg-black/40 border border-white/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#B580FF]/60"
        />
        <p className="text-xs text-white/45 mt-2">
          Leave blank to auto-generate from track + artist name.
        </p>
      </div>

      <div className="flex gap-2 justify-end">
        <button
          data-testid="promo-cancel"
          onClick={onCancel}
          className="px-5 py-2.5 text-sm font-semibold text-white/70 hover:text-white transition"
        >
          Cancel
        </button>
        <button
          data-testid="promo-submit"
          onClick={submit}
          disabled={submitting}
          className="bg-white text-black font-semibold rounded-full px-6 py-2.5 hover:scale-[1.02] transition disabled:opacity-50"
        >
          {submitting ? "Launching…" : `Launch · $${Number(budget).toFixed(2)}`}
        </button>
      </div>
    </div>
  );
};

export default function Promote() {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/ads/promote/my-campaigns");
      setCampaigns(r.data?.results || []);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) load();
    else setLoading(false);
  }, [user, load]);

  const pause = async (id) => {
    try {
      const r = await api.post(`/ads/promote/${id}/pause`);
      toast.success(`Campaign ${r.data.status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update");
    }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel campaign and refund remaining budget to your earnings?")) return;
    try {
      const r = await api.post(`/ads/promote/${id}/refund-remaining`);
      toast.success(`Refunded $${r.data.refunded_usd.toFixed(2)}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't refund");
    }
  };

  if (!user) {
    return (
      <div
        data-testid="promote-signin-required"
        className="px-4 sm:px-8 py-16 max-w-xl mx-auto text-center"
      >
        <Megaphone size={40} className="mx-auto text-[#B580FF] mb-3" />
        <h1 className="font-display text-3xl font-semibold mb-2">Sign in to run promotions</h1>
        <p className="text-white/55 text-sm">
          Promote your tracks to listeners in your target genres.
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="promote-page"
      className="px-4 sm:px-8 py-10 lg:py-14 max-w-3xl mx-auto"
    >
      <header className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <Megaphone size={14} className="text-[#B580FF]" />
          <span className="text-[10px] uppercase tracking-[0.28em] text-white/40">
            artist marketing
          </span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">
          Promote your music
        </h1>
        <p className="text-white/55 text-base sm:text-lg mt-3 max-w-xl">
          Pay $0.05 per impression to play a 15-second promo of your track between songs for
          free-tier listeners in your target genres. Funded directly from your earned balance.
        </p>
      </header>

      {!showNew && (
        <button
          data-testid="new-campaign-btn"
          onClick={() => setShowNew(true)}
          className="mb-8 bg-white text-black font-semibold rounded-full px-6 py-3 flex items-center gap-2 hover:scale-[1.02] transition"
        >
          <Plus size={16} />
          New campaign
        </button>
      )}

      {showNew && (
        <div className="mb-10">
          <NewCampaignForm
            onCancel={() => setShowNew(false)}
            onCreated={() => {
              setShowNew(false);
              load();
            }}
          />
        </div>
      )}

      {loading ? (
        <div className="text-white/40">Loading campaigns…</div>
      ) : campaigns.length === 0 ? (
        <div className="text-center bg-white/[0.03] border border-white/10 rounded-3xl p-12">
          <Sparkles size={32} className="mx-auto text-white/30 mb-3" />
          <h3 className="font-display text-xl font-semibold mb-1">No campaigns yet</h3>
          <p className="text-white/50 text-sm max-w-md mx-auto">
            Launch your first promo above. We&apos;ll start playing 15-second clips of your track to
            free-tier listeners in your target genres immediately.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/55 font-semibold mb-3">
            <TrendingUp size={13} />
            Active and past campaigns
          </div>
          {campaigns.map((c) => (
            <CampaignRow
              key={c.campaign_id}
              campaign={c}
              onPause={pause}
              onCancel={cancel}
            />
          ))}
        </div>
      )}
    </div>
  );
}
